import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, uploadFile, ApiError } from '../shared/api';
import { formatMoney, formatNumber } from '../shared/formatting';

type Locale = 'hr' | 'en';

type Group = { id: number; label: string; metal_type: 'gold' | 'silver'; fine_per_piece_g: number; fine_source?: '110' | 'asortiman'; skus: string[]; sort_order: number; is_active: boolean };
type Assortment = { id: number; code: string; name: string; is_central: boolean };
type Branch = { code: string; name: string; is_central: boolean; assortment_id: number | null };
type Config = {
    active: boolean; groups: Group[]; assortments: Assortment[];
    limits: Record<string, { min_qty: number; max_qty: number }>;
    branches: Branch[]; unmatched: { id: number; source_name: string; assortment_id: number }[];
    can_edit: boolean;
};
type GapRow = { warehouse_code: string; warehouse_name: string; group_label: string; min_qty: number; max_qty: number; stock_qty: number; gap_qty: number; gap_fine_g: number; gap_value_eur: number };
type Gap = { rows: GapRow[]; central: (Omit<GapRow, 'gap_qty' | 'gap_fine_g' | 'gap_value_eur'>)[]; stock_as_of: string | null };
type TurnoverRow = {
    warehouse_code: string; warehouse_name: string; assortment_name: string; capacity_qty: number;
    sold_qty: number; sold_compare_qty: number | null; turnover: number | null; change: number | null;
    proposal: 'visi' | 'nizi' | 'ostaje'; target_assortment_name: string | null;
};
type Turnover = { month: string; method: string; first_sale_date: string | null; period: [string, string]; compare_period: [string, string] | null; threshold: number; rows: TurnoverRow[] };

/**
 * 40.8 po asortimanima (N9, TASK-40-61; mail 15.09.2026, prilog
 * Prijedlog_lagera). Tri tablice koje se uredjuju s vremena na vrijeme:
 * vrste artikala sa siframa (nova vrsta se dodaje u zadnjem retku), KOM
 * MIN/MAX po asortimanu, poslovnice po asortimanu. Ispod je prijedlog
 * nadopune: poslovnica na MIN ili ispod dobiva dopunu do MAX; isti izracun
 * koristi PG2 u 40.42.
 */
export function LagerAssortmentsPanel({ locale, token, metal }: { locale: Locale; token: string | null; metal: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [cfg, setCfg] = useState<Config | null>(null);
    const [gap, setGap] = useState<Gap | null>(null);
    const [turnover, setTurnover] = useState<Turnover | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [limitEdits, setLimitEdits] = useState<Record<string, string>>({});
    const [skuEdits, setSkuEdits] = useState<Record<number, string>>({});
    const [groupEdits, setGroupEdits] = useState<Record<string, string>>({});
    const [newGroup, setNewGroup] = useState({ label: '', fine: '', skus: '' });
    const [addBranch, setAddBranch] = useState({ code: '', assortmentId: '' });

    const load = useCallback(async () => {
        try {
            const [c, g, tr] = await Promise.all([
                api<Config>('/lager-plan/assortments', token),
                api<Gap>(`/lager-plan/assortments/gap?metal_type=${metal}`, token),
                api<Turnover>('/lager-plan/assortments/turnover', token),
            ]);
            setCfg(c);
            setGap(g);
            setTurnover(tr);
            setLimitEdits({});
            setSkuEdits({});
            setGroupEdits({});
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
        }
    }, [token, metal]);
    useEffect(() => { void load(); }, [load]);

    const run = async (fn: () => Promise<unknown>, ok: string) => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await fn();
            setNotice(ok);
            await load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const importFile = (file: File) => {
        const fd = new FormData();
        fd.append('file', file);
        void run(async () => {
            const r = await uploadFile<{ groups: number; limits: number; branches_matched: number; branches_unmatched: string[]; branches_duplicate?: { name: string; code: string }[]; unknown_skus?: { group: string; code: string }[] }>('/lager-plan/assortments/import', token, fd);
            const dup = r.branches_duplicate ?? [];
            const unk = r.unknown_skus ?? [];
            setNotice(`${r.groups} ${t('vrsta', 'groups')}, ${r.limits} ${t('granica', 'limits')}, ${r.branches_matched} ${t('poslovnica spojeno', 'branches matched')}${r.branches_unmatched.length ? `; ${t('nije spojeno', 'unmatched')}: ${r.branches_unmatched.join(', ')}` : ''}${dup.length ? `; ${t('navedeno više puta, preskočeno', 'listed more than once, skipped')}: ${dup.map((d) => `${d.name} (${d.code})`).join(', ')}` : ''}${unk.length ? `; ${t('šifre nisu u 110, izostavljene', 'codes not in 110, skipped')}: ${unk.map((u) => `${u.code} (${u.group})`).join(', ')}` : ''}`);
        }, t('Uvoz gotov.', 'Import done.'));
    };

    const saveLimit = (a: Assortment, g: Group) => {
        const key = `${a.id}|${g.id}`;
        const cur = cfg?.limits[key] ?? { min_qty: 0, max_qty: 0 };
        const min = Number((limitEdits[`${key}|min`] ?? String(cur.min_qty)).replace(',', '.'));
        const max = Number((limitEdits[`${key}|max`] ?? String(cur.max_qty)).replace(',', '.'));
        void run(() => mutate('/lager-plan/assortments/limit', token, 'PUT', { assortment_id: a.id, article_group_id: g.id, min_qty: min, max_qty: max }), t('Spremljeno.', 'Saved.'));
    };

    const splitSkus = (s: string) => s.split(/[,\s]+/).filter(Boolean);

    const saveGroup = (g: Group, patch: Partial<Pick<Group, 'label' | 'fine_per_piece_g' | 'skus'>>) =>
        void run(() => mutate('/lager-plan/assortments/group', token, 'PUT', { id: g.id, label: g.label, metal_type: g.metal_type, fine_per_piece_g: g.fine_per_piece_g, skus: g.skus, sort_order: g.sort_order, is_active: g.is_active, ...patch }), t('Spremljeno.', 'Saved.'));

    const saveSkus = (g: Group) => saveGroup(g, { skus: splitSkus(skuEdits[g.id] ?? g.skus.join(', ')) });

    const addGroup = () => {
        const fine = Number(newGroup.fine.replace(',', '.'));
        if (newGroup.label.trim() === '' || !(fine > 0)) {
            setError(t('Nova vrsta treba naziv i fine grame po komadu.', 'A new item group needs a name and fine grams per piece.'));
            return;
        }
        const sort = Math.max(0, ...(cfg?.groups ?? []).map((g) => g.sort_order)) + 10;
        void run(async () => {
            await mutate('/lager-plan/assortments/group', token, 'PUT', { label: newGroup.label.trim(), metal_type: metal === 'silver' ? 'silver' : 'gold', fine_per_piece_g: fine, skus: splitSkus(newGroup.skus), sort_order: sort, is_active: true });
            setNewGroup({ label: '', fine: '', skus: '' });
        }, t('Vrsta dodana; upiši MIN / MAX po asortimanu.', 'Item group added; enter MIN / MAX per assortment.'));
    };

    const assign = (code: string, assortmentId: number | null, unmatchedId?: number) =>
        void run(() => mutate('/lager-plan/assortments/branch', token, 'PUT', { warehouse_code: code, assortment_id: assortmentId, unmatched_id: unmatchedId ?? null }), t('Spremljeno.', 'Saved.'));

    if (!cfg) return error ? <p className="error">{error}</p> : <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    const canEdit = cfg.can_edit;
    const groups = cfg.groups.filter((g) => g.metal_type === (metal === 'silver' ? 'silver' : 'gold'));
    const unassigned = cfg.branches.filter((b) => !b.is_central && b.assortment_id === null);

    return (
        <div>
            <p className="muted">
                {cfg.active
                    ? t('Asortimani su aktivni: PG2 (40.42) predlaže nadopunu po ovim tablicama.', 'Assortments are active: PG2 (40.42) proposes restock from these tables.')
                    : t('Nijedna poslovnica nije u asortimanu; PG2 i dalje radi po staroj matrici.', 'No branch is assigned; PG2 still uses the old matrix.')}
            </p>
            {canEdit ? (
                <label style={{ display: 'inline-block', marginBottom: '0.75rem' }}>
                    {t('Uvoz iz priloga Prijedlog_lagera (xlsx)', 'Import from Prijedlog_lagera (xlsx)')}{' '}
                    <input type="file" accept=".xlsx" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) importFile(f); e.target.value = ''; }} />
                </label>
            ) : null}
            {error ? <p className="error">{error}</p> : null}
            {notice ? <p className="muted">{notice}</p> : null}

            <h3>{t('Nadopuna po poslovnici', 'Restock by branch')}</h3>
            {gap && gap.rows.length > 0 ? (
                <table className="admin-table">
                    <thead><tr><th>{t('Poslovnica', 'Branch')}</th><th>{t('Vrsta', 'Item group')}</th><th>MIN</th><th>MAX</th><th>{t('Slobodno', 'Free')}</th><th>{t('Dopuna kom', 'Restock qty')}</th><th>{t('Fino g', 'Fine g')}</th><th>{t('Po spotu', 'At spot')}</th></tr></thead>
                    <tbody>
                        {gap.rows.map((r) => (
                            <tr key={`${r.warehouse_code}|${r.group_label}`}>
                                <td>{r.warehouse_code} · {r.warehouse_name}</td><td>{r.group_label}</td>
                                <td>{formatNumber(r.min_qty, locale)}</td><td>{formatNumber(r.max_qty, locale)}</td><td>{formatNumber(r.stock_qty, locale)}</td>
                                <td><strong>{formatNumber(r.gap_qty, locale)}</strong></td><td>{formatNumber(r.gap_fine_g, locale)}</td><td>{formatMoney(r.gap_value_eur, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : <p className="muted">{t('Nijedna poslovnica nije na MIN ili ispod.', 'No branch is at or below MIN.')}</p>}
            {gap && gap.central.length > 0 ? (
                <p className="muted">{t('Centralno (211) se dopunjuje u PG2 nakon prijenosa u poslovnice.', 'Central (211) is replenished in PG2 after transfers to branches.')}</p>
            ) : null}

            <h3>{t('KOM MIN / MAX po asortimanu', 'Qty MIN / MAX by assortment')}</h3>
            <div className="table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t('Vrsta artikla', 'Item group')}</th><th>{t('Fino g/kom', 'Fine g/pc')}</th><th>{t('Šifre', 'SKUs')}</th>
                            {cfg.assortments.map((a) => <th key={a.id}>{a.name}<br /><span className="muted">MIN / MAX</span></th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((g) => (
                            <tr key={g.id}>
                                <td>
                                    {canEdit ? (
                                        <input value={groupEdits[`${g.id}|label`] ?? g.label} disabled={busy} style={{ width: 200 }}
                                            onChange={(e) => setGroupEdits((p) => ({ ...p, [`${g.id}|label`]: e.target.value }))}
                                            onBlur={() => { const v = groupEdits[`${g.id}|label`]; if (v !== undefined && v.trim() !== '' && v !== g.label) saveGroup(g, { label: v.trim() }); }} />
                                    ) : g.label}
                                </td>
                                <td>
                                    {canEdit && g.fine_source !== '110' ? (
                                        <input value={groupEdits[`${g.id}|fine`] ?? String(g.fine_per_piece_g)} disabled={busy} style={{ width: 60 }}
                                            onChange={(e) => setGroupEdits((p) => ({ ...p, [`${g.id}|fine`]: e.target.value }))}
                                            onBlur={() => { const v = groupEdits[`${g.id}|fine`]; const n = Number((v ?? '').replace(',', '.')); if (v !== undefined && n > 0 && n !== g.fine_per_piece_g) saveGroup(g, { fine_per_piece_g: n }); }} />
                                    ) : <span title={g.fine_source === '110' ? t('Iz šifrarnika 110 (sve šifre iste mase)', 'From codebook 110 (all codes same weight)') : undefined}>{formatNumber(g.fine_per_piece_g, locale)}{g.fine_source === '110' ? ' (110)' : ''}</span>}
                                </td>
                                <td>
                                    {canEdit ? (
                                        <input value={skuEdits[g.id] ?? g.skus.join(', ')} disabled={busy} style={{ width: 170 }}
                                            onChange={(e) => setSkuEdits((p) => ({ ...p, [g.id]: e.target.value }))}
                                            onBlur={() => { if (skuEdits[g.id] !== undefined && skuEdits[g.id] !== g.skus.join(', ')) saveSkus(g); }} />
                                    ) : g.skus.join(', ')}
                                </td>
                                {cfg.assortments.map((a) => {
                                    const key = `${a.id}|${g.id}`;
                                    const lim = cfg.limits[key] ?? { min_qty: 0, max_qty: 0 };
                                    if (!canEdit) return <td key={a.id}>{formatNumber(lim.min_qty, locale)} / {formatNumber(lim.max_qty, locale)}</td>;
                                    const changed = limitEdits[`${key}|min`] !== undefined || limitEdits[`${key}|max`] !== undefined;
                                    return (
                                        <td key={a.id} style={{ whiteSpace: 'nowrap' }}>
                                            <input value={limitEdits[`${key}|min`] ?? String(lim.min_qty)} disabled={busy} style={{ width: 44 }}
                                                onChange={(e) => setLimitEdits((p) => ({ ...p, [`${key}|min`]: e.target.value }))} />
                                            {' / '}
                                            <input value={limitEdits[`${key}|max`] ?? String(lim.max_qty)} disabled={busy} style={{ width: 44 }}
                                                onChange={(e) => setLimitEdits((p) => ({ ...p, [`${key}|max`]: e.target.value }))} />
                                            {changed ? <button type="button" className="compact" disabled={busy} onClick={() => saveLimit(a, g)}>✓</button> : null}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {canEdit ? (
                            <tr>
                                <td><input placeholder={t('nova vrsta artikla', 'new item group')} value={newGroup.label} disabled={busy} style={{ width: 200 }} onChange={(e) => setNewGroup((p) => ({ ...p, label: e.target.value }))} /></td>
                                <td><input placeholder="g" value={newGroup.fine} disabled={busy} style={{ width: 60 }} onChange={(e) => setNewGroup((p) => ({ ...p, fine: e.target.value }))} /></td>
                                <td><input placeholder={t('šifre, zarezom', 'SKUs, comma separated')} value={newGroup.skus} disabled={busy} style={{ width: 170 }} onChange={(e) => setNewGroup((p) => ({ ...p, skus: e.target.value }))} /></td>
                                <td colSpan={cfg.assortments.length}><button type="button" className="compact" disabled={busy} onClick={addGroup}>{t('Dodaj vrstu', 'Add item group')}</button></td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>

            <h3>{t('Prijedlog promjene asortimana po obrtaju', 'Assortment change proposal by turnover')}</h3>
            {turnover ? (
                <>
                    <p className="muted">
                        {turnover.method === 'isti_mjesec_prosle_godine'
                            ? t('Usporedba: isti mjesec prošle godine', 'Comparison: same month last year')
                            : turnover.method === 'zadnja_3_prema_prethodna_3'
                                ? t('Usporedba: zadnja 3 mjeseca prema prethodna 3 (isti mjesec prošle godine još nema povijesti)', 'Comparison: last 3 months vs previous 3 (no same-month history yet)')
                                : t('Nema dovoljno povijesti prometa za usporedbu, pa sve poslovnice ostaju u svom asortimanu', 'Not enough sales history to compare, so every branch stays in its assortment')}
                        {' · '}{turnover.period[0]} - {turnover.period[1]}
                        {turnover.compare_period ? ` / ${turnover.compare_period[0]} - ${turnover.compare_period[1]}` : ''}
                        {' · '}{t('prag', 'threshold')} {formatNumber(turnover.threshold * 100, locale)} %
                        {turnover.first_sale_date ? ` · ${t('promet od', 'sales since')} ${turnover.first_sale_date}` : ''}
                    </p>
                    {turnover.rows.length > 0 ? (
                        <table className="admin-table">
                            <thead><tr><th>{t('Poslovnica', 'Branch')}</th><th>{t('Asortiman', 'Assortment')}</th><th>{t('Kapacitet kom', 'Capacity qty')}</th><th>{t('Prodano kom', 'Sold qty')}</th><th>{t('Usporedno kom', 'Compare qty')}</th><th>{t('Obrtaj / mj', 'Turnover / mo')}</th><th>{t('Promjena', 'Change')}</th><th>{t('Prijedlog', 'Proposal')}</th></tr></thead>
                            <tbody>
                                {turnover.rows.map((r) => (
                                    <tr key={r.warehouse_code}>
                                        <td>{r.warehouse_code} · {r.warehouse_name}</td><td>{r.assortment_name}</td>
                                        <td>{formatNumber(r.capacity_qty, locale)}</td><td>{formatNumber(r.sold_qty, locale)}</td>
                                        <td>{r.sold_compare_qty === null ? '-' : formatNumber(r.sold_compare_qty, locale)}</td>
                                        <td>{r.turnover === null ? '-' : formatNumber(r.turnover, locale)}</td>
                                        <td>{r.change === null ? '-' : `${formatNumber(r.change * 100, locale)} %`}</td>
                                        <td>{r.proposal === 'ostaje' ? t('ostaje', 'keep') : `${r.proposal === 'visi' ? t('viši', 'higher') : t('niži', 'lower')}: ${r.target_assortment_name ?? ''}`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <p className="muted">{t('Nijedna poslovnica nije dodijeljena asortimanu.', 'No branch is assigned to an assortment.')}</p>}
                </>
            ) : null}

            <h3>{t('Poslovnice po asortimanu', 'Branches by assortment')}</h3>
            {cfg.unmatched.length > 0 ? (
                <div>
                    <p className="muted">{t('Iz priloga nije spojeno sa skladištem', 'Not matched to a warehouse')}:</p>
                    <ul>
                        {cfg.unmatched.map((u) => (
                            <li key={u.id}>
                                {u.source_name} ({cfg.assortments.find((a) => a.id === u.assortment_id)?.name ?? ''})
                                {canEdit ? (
                                    <>
                                        {' '}
                                        <select value="" disabled={busy} onChange={(e) => { if (e.target.value) assign(e.target.value, u.assortment_id, u.id); }}>
                                            <option value="">{t('upari sa skladištem…', 'pair with warehouse…')}</option>
                                            {unassigned.map((b) => <option key={b.code} value={b.code}>{b.code} · {b.name}</option>)}
                                        </select>
                                    </>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {canEdit ? (
                <p>
                    {t('Dodaj poslovnicu', 'Add branch')}{' '}
                    <select value={addBranch.code} disabled={busy} onChange={(e) => setAddBranch((p) => ({ ...p, code: e.target.value }))}>
                        <option value="">{t('poslovnica bez asortimana…', 'branch without assortment…')}</option>
                        {unassigned.map((b) => <option key={b.code} value={b.code}>{b.code} · {b.name}</option>)}
                    </select>{' '}
                    <select value={addBranch.assortmentId} disabled={busy} onChange={(e) => setAddBranch((p) => ({ ...p, assortmentId: e.target.value }))}>
                        <option value="">{t('asortiman…', 'assortment…')}</option>
                        {cfg.assortments.filter((a) => !a.is_central).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>{' '}
                    <button type="button" className="compact" disabled={busy || addBranch.code === '' || addBranch.assortmentId === ''}
                        onClick={() => { assign(addBranch.code, Number(addBranch.assortmentId)); setAddBranch({ code: '', assortmentId: '' }); }}>
                        {t('Dodaj', 'Add')}
                    </button>
                </p>
            ) : null}
            <table className="admin-table">
                <thead><tr><th>{t('Skladište', 'Warehouse')}</th><th>{t('Asortiman', 'Assortment')}</th></tr></thead>
                <tbody>
                    {cfg.branches.map((b) => (
                        <tr key={b.code}>
                            <td>{b.code} · {b.name}{b.is_central ? ` (${t('centralno', 'central')})` : ''}</td>
                            <td>
                                {canEdit ? (
                                    <select value={b.assortment_id ?? ''} disabled={busy} onChange={(e) => assign(b.code, e.target.value ? Number(e.target.value) : null)}>
                                        <option value="">{t('nije u asortimanu', 'not assigned')}</option>
                                        {cfg.assortments.filter((a) => a.is_central === b.is_central).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                ) : (cfg.assortments.find((a) => a.id === b.assortment_id)?.name ?? '-')}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
